import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccessList,
  useDateFns,
  useHasAccess,
  useIntegrations,
  useNavigation,
  useNavigationContext,
  useProvider,
} from '~/Hooks';
import { ItemCreationFieldValue, ItemCreationSection } from '~/Components';
import { GeneralSection } from '../EventCreateSections/GeneralSection';
import { EventDetailsSection } from '../EventCreateSections/EventDetailsSection/EventDetailsSection';
import { EventScheduleSection } from '../EventCreateSections/EventScheduleSection/EventScheduleSection';
import { SettingsSection } from '../EventCreateSections/SettingsSection';
import { useRequest } from '~/Hooks/useRequest';
import { EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { routeReplacer } from '~/Helpers/String.helper';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { ExtendedEvent } from '../ExtendedEvent.model';
import { getEventById } from '../APIs';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { useBookingSummary } from '~/store/bookings';
import { Route } from '~/Constants/Routes.constants';
import { markEventSectionsDone } from '../helpers/validation';
import { IBookingSummaryEvent } from '~/Models/DTO/Booking.models';
import { useEventCreateHeaders } from './useEventCreateHeaders';
import { EventType } from '~/Models/DTO/Event.model';
import { Segment } from '~/Helpers/Segment.helper';
import { SegmentName } from '~/Constants/Segment.constants';
import { ExternalPartyID } from '~/Models/DTO/externalParties.model';
import { useQueryClient } from 'react-query';
import { useLocations } from '~/store/provider';

const getSections = (
  eventGroup: ExtendedEventGroup,
  bookingSummary: IBookingSummaryEvent,
  setFieldValue: ItemCreationFieldValue,
  isLoading: boolean,
  eventType: EventType | string,
  liveStreamEnabled: boolean,
): ItemCreationSection<ExtendedEvent | ExtendedEventGroup>[] => [
  ({ data }) => (
    <GeneralSection
      isLoading={isLoading}
      eventGroup={eventGroup}
      event={data as ExtendedEvent}
      bookingSummary={bookingSummary}
      eventType={(eventType || eventGroup.type || data.type) as EventType}
      liveStreamEnabled={liveStreamEnabled}
    />
  ),
  ({ data }) => <EventDetailsSection event={data as ExtendedEvent} eventGroup={eventGroup} />,
  ({ data }) => (
    <EventScheduleSection
      setFieldValue={setFieldValue}
      event={data as ExtendedEvent}
      eventGroup={eventGroup}
      eventType={(eventType || eventGroup.type || data.type) as EventType}
    />
  ),
  ({ data }) => <SettingsSection event={data as ExtendedEvent} eventGroup={eventGroup} />,
];
const initialDate = '1990/01/01';

export const useEventEdit = () => {
  // we need to send all the values for initializing the form.
  // Otherwise formik will complain that the components are switching between a controlled and uncontrolled state
  const [initialValues, setInitialValues] = useState<ExtendedEvent>({
    startDate: initialDate,
    startTime: initialDate,
    endTime: initialDate,
  } as ExtendedEvent);
  const [event, setEvent] = useState<ExtendedEvent>({} as ExtendedEvent);
  const [eventGroup, setEventGroup] = useState<ExtendedEventGroup>({} as ExtendedEventGroup);
  //hooks
  const { request, requestWithCallback } = useRequest();
  const { t } = useTranslation();
  const { dangerouslyPushBack, getParams } = useNavigation();
  const { domain, id, eventType } = getParams<{
    eventType: EventType;
    domain: string;
    id: string;
  }>();
  const { currentProvider, isUsingThePartnerTool } = useProvider();
  const { bookingSummary } = useBookingSummary(event.id);
  const { parse, formatDate } = useDateFns();
  const { hasAccess } = useHasAccess();
  const { hasIntegration } = useIntegrations();
  const { blockNavigation } = useNavigationContext();
  const isUSCIntegrationEnabled = useMemo(() => hasIntegration(ExternalPartyID.USC_INTEGRATION), [
    hasIntegration,
  ]);
  const queryClient = useQueryClient();

  //state
  const [isLoading, setIsLoading] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [isGeneralSectionDone, setIsGeneralSectionDone] = useState(false);
  const [submissionError, setSubmissionError] = useState<IErrorResponse>({});
  const [showFormErrors, setShowFormErrors] = useState(false);

  const isForcedEventGroupUSCSync = hasAccess(AccessList.FORCE_EVENT_GROUP_USC_SYNC);
  const setFieldValue = (field: string, value: string) => {
    setEvent((prevEvent) => ({
      ...prevEvent,
      [field]: value,
    }));
  };

  const headers = useEventCreateHeaders({ eventType, isGeneralSectionDone });
  const { locationList } = useLocations();
  const currentLocation = locationList.find((location) => location.id === eventGroup?.locationId);

  const liveStreamEnabled = !!currentLocation?.liveStreamEnabled || !isUsingThePartnerTool;

  const sections = useMemo(
    () =>
      getSections(
        eventGroup,
        bookingSummary,
        setFieldValue,
        isLoading,
        eventType,
        liveStreamEnabled,
      ),
    [eventGroup, bookingSummary, isLoading, eventType, liveStreamEnabled],
  );
  const validate = (newEvent: ExtendedEvent) => {
    markEventSectionsDone(newEvent, bookingSummary, setIsGeneralSectionDone);
    setEvent((prevEvent) => ({
      ...prevEvent,
      ...newEvent,
    }));
  };

  const addMissingValues = (prevEvent: ExtendedEvent) => {
    const startField = `${formatDate(new Date(prevEvent.startDate).toISOString(), 'yyyy-MM-dd')}T${
      prevEvent.startTime.split('T')[1]
    }`;
    const endField = `${formatDate(new Date(prevEvent.startDate).toISOString(), 'yyyy-MM-dd')}T${
      prevEvent.endTime.split('T')[1]
    }`;
    const newEvent = { ...prevEvent, start: startField, end: endField };

    if (eventType === EventType.FREE_TRAINING) {
      Object.assign(newEvent, { seats: null });
    }

    return newEvent;
  };

  const onSave = useCallback(async () => {
    if (disableSubmit) {
      setShowFormErrors(true);
      return;
    }

    setIsLoading(true);

    if (currentProvider) {
      const { email, domain } = currentProvider;
      Segment.track(SegmentName.EDIT_SINGLE_EVENT_SAVE, {
        email,
        domain,
        eventType,
        isUsingPartnerPortal: isUsingThePartnerTool,
      });
    }
    try {
      const fullEvent = addMissingValues(event);
      await requestWithCallback<EventGroup>(
        {
          url: routeReplacer(EVENT_SERVICE_URLS.EVENT_DETAIL, { id: event.id }),
          method: 'PUT',
          data: { domain, ...fullEvent, providerId: currentProvider?.uuid || '' },
          actionName: REQUEST_ACTIONS.SINGLE_EVENT,
        },
        async () => {
          queryClient.invalidateQueries(REQUEST_ACTIONS.EVENTCATEGORIES);
          blockNavigation(false);
          dangerouslyPushBack(routeReplacer(Route.EVENT_DETAILS, { id: event.id, eventType }));
        },
        (error) => {
          setSubmissionError(error);
          setIsLoading(false);
        },
      );
    } catch (err) {
      console.error('failed to save the event', err);
    }
  }, [event, eventType, currentProvider, dangerouslyPushBack, requestWithCallback]);

  useEffect(() => {
    setIsLoading(true);
    getEventById(
      request,
      requestWithCallback,
      id || '',
      isUSCIntegrationEnabled,
      isForcedEventGroupUSCSync,
      setInitialValues,
      setEventGroup,
      setSubmissionError,
      formatDate,
      parse,
      () => setIsLoading(false),
    );
  }, [id]);

  useEffect(() => {
    setDisableSubmit(!isGeneralSectionDone);
  }, [isGeneralSectionDone]);

  useEffect(() => {
    validate(initialValues);
  }, [initialValues]);

  return {
    eventType,
    eventName: event.name,
    eventId: event.id,
    initialValues,
    title: t('app.edit'),
    eventGroupName: eventGroup.name,
    onSave,
    validate,
    isLoading,
    listItems: sections,
    headers,
    error: submissionError,
    disableSubmit,
    showFormErrors,
  };
};
